import { createStyles } from '@mantine/core';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import PromotionsModalsContextProvider from '~/domains/promotions/Modals/PromotionsModalsProvider';

import { useModalFocusTrap } from '../hooks/useModalFocusTrap';

import { DepositView } from './DepositView';

const useStyles = createStyles((theme) => ({
  container: {
    width: 780,

    [theme.fn.smallerThan('sm')]: {
      width: '100%',
    },
  },
}));

type DepositModalProps = {
  isOpen?: boolean;
  onClose: VoidFunction;
  contestIdForPostDeposit?: string;
  fallbackUrl?: string;
  selectedBonusId?: string;
  redirectTo?: string;
};


function DepositModalContent({
  isOpen,
  onClose,
  contestIdForPostDeposit,
  fallbackUrl,
  selectedBonusId,
  redirectTo,
}: DepositModalProps & { isOpen: boolean }) {
  const { classes } = useStyles();
  // EDGE-32156: the design-system Dialog/DialogModal renders a plain inline overlay with no focus
  // trap, so Tab escaped into the page behind. Trap focus at the deposit container (benefits every
  // deposit method, which all render inside this one modal) via a node-scoped native trap that
  // coexists with the portaled crypto reveal sheet's own Mantine trap. See useModalFocusTrap.
  const focusTrapRef = useModalFocusTrap<HTMLDivElement>(isOpen);

  return (
    // `display: contents` is load-bearing: this wrapper sits DIRECTLY inside DialogBackdrop's 1fr×1fr
    // grid (the modal manager renders DepositModal inside the backdrop). A boxed wrapper would become
    // the grid item and stretch to fill the cell, which (a) breaks `.modal`'s alignSelf/justifySelf
    // centering (it'd no longer be the direct grid item) and (b) covers the backdrop so click-outside
    // can't reach it (DialogBackdrop closes only on target===currentTarget). `display: contents`
    // generates no box, so `.modal` stays the direct grid item and the wrapper has no hit surface —
    // while the DOM node itself remains for the focus trap's ref / keydown / node.contains.
    <div ref={focusTrapRef} style={{ display: 'contents' }}>
      <Dialog
        onClose={onClose}
        isOpen={isOpen}
        type={{
          desktop: 'modal',
          mobile: 'modal-full-screen',
        }}
        className={classes.container}
      >
        <DialogBody>
          <DepositView
            contestIdForPostDeposit={contestIdForPostDeposit}
            fallbackUrl={fallbackUrl}
            selectedBonusId={selectedBonusId}
            redirectTo={redirectTo}
          />
        </DialogBody>
      </Dialog>
    </div>
  );
}

function DepositModal({ isOpen = true, ...rest }: DepositModalProps) {
  return (
    <PromotionsModalsContextProvider>
      <DepositModalContent isOpen={isOpen} {...rest} />
    </PromotionsModalsContextProvider>
  );
}

export default DepositModal;
